// "Customer messages": the conversation with the seller on one quote, oldest to
// newest, with a Reply box under the latest customer message. ONE implementation,
// used on Quote Assessment, the deal modal and Issue Trades.
//
//   <CustomerMessagesPanel dealId quoteId items />   fetches for itself
//   <CustomerMessagesView conversation items />      for a screen that already
//                                                    called useCustomerMessages
//                                                    (Issue Trades also reads the
//                                                    item decisions from it)
//
// `items` is [{ quote_item_id, name }], used only to name an item in a decision line.

import { useState } from 'react';
import { formatCurrencyFromCents, formatDateTime } from '../../../utils/api';
import {
    KIND_LABELS, MAX_REPLY_LENGTH, latestCustomerMessage, useCustomerMessages,
} from './customerMessages.js';

/** The one line an item decision reads as. */
export function decisionText(message, items) {
    const name = items?.find((i) => i.quote_item_id === message.details?.quote_item_id)?.name || 'an item';
    return message.details?.decision === 'accept'
        ? `Customer accepted ${formatCurrencyFromCents(message.details.price_cents)} for ${name}`
        : `Customer asked for ${name} back`;
}

function MessageRow({ message, items }) {
    const fromCustomer = message.direction === 'from_customer';
    const who = fromCustomer ? 'Customer' : `Us${message.created_by ? ` (${message.created_by})` : ''}`;
    const text = message.kind === 'item_decision' ? decisionText(message, items) : message.body;
    return (
        <li
            data-testid="customer-message"
            data-direction={message.direction}
            style={{
                listStyle: 'none',
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-2)',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--color-border)',
                background: fromCustomer ? 'var(--color-bg-secondary)' : 'var(--color-bg)',
                marginLeft: fromCustomer ? 0 : 'var(--space-4)',
                marginRight: fromCustomer ? 'var(--space-4)' : 0,
            }}
        >
            <div className="text-muted" style={{ fontSize: '0.75rem', marginBottom: 'var(--space-1)' }}>
                <strong>{who}</strong> · {formatDateTime(message.sent_at)} · {KIND_LABELS[message.kind] ?? message.kind}
            </div>
            {text && <div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>}
        </li>
    );
}

function ReplyBox({ conversation }) {
    const { replyEnabled, messages, reply, reload } = conversation;
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const target = latestCustomerMessage(messages);
    if (!target) return null;

    async function send() {
        setSending(true);
        setError(null);
        try {
            await reply(text.trim(), target.quote_customer_message_id);
            setText('');
            reload();
        } catch (e) {
            setError(e?.message || 'The reply could not be sent');
        } finally {
            setSending(false);
        }
    }

    const blank = text.trim().length === 0;
    return (
        <div style={{ marginTop: 'var(--space-3)' }}>
            <label htmlFor="customer-reply" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                Reply to the customer
            </label>
            <textarea
                id="customer-reply"
                className="form-input"
                rows={3}
                maxLength={MAX_REPLY_LENGTH}
                value={text}
                disabled={!replyEnabled || sending}
                onChange={(e) => setText(e.target.value)}
                placeholder="The customer gets this by email and on their quote page"
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={!replyEnabled || sending || blank}
                    onClick={send}
                >
                    {sending ? 'Sending…' : 'Send'}
                </button>
                <span className="text-muted" style={{ fontSize: '0.75rem' }}>{text.length}/{MAX_REPLY_LENGTH}</span>
                {!replyEnabled && (
                    <span className="badge badge-neutral" data-testid="reply-off">Replies are switched off</span>
                )}
            </div>
            {error && <div role="alert" className="qa-error" style={{ marginTop: 'var(--space-2)' }}>{error}</div>}
        </div>
    );
}

export function CustomerMessagesView({ conversation, items }) {
    const { status, messages } = conversation;
    return (
        <section className="card" data-testid="customer-messages" style={{ padding: 'var(--space-3)' }}>
            <div className="qa-lab">Customer messages</div>
            {status === 'loading' && <div className="qa-muted">Loading messages…</div>}
            {status === 'error' && (
                <div className="qa-muted" role="alert">
                    The messages could not be loaded.{' '}
                    <button type="button" className="btn btn-sm" onClick={conversation.reload}>Try again</button>
                </div>
            )}
            {status === 'ready' && messages.length === 0 && <div className="qa-muted">No messages yet.</div>}
            {status === 'ready' && messages.length > 0 && (
                <ul style={{ margin: 0, padding: 0 }}>
                    {messages.map((m) => <MessageRow key={m.quote_customer_message_id} message={m} items={items} />)}
                </ul>
            )}
            {status === 'ready' && <ReplyBox conversation={conversation} />}
        </section>
    );
}

export default function CustomerMessagesPanel({ dealId, quoteId, items }) {
    const conversation = useCustomerMessages(dealId, quoteId);
    return <CustomerMessagesView conversation={conversation} items={items} />;
}
